import React, { useMemo, useEffect } from 'react';
import { AuthenticatedDatasetImage } from './AuthenticatedDatasetImage';
import { MarketplaceCard } from './MarketplaceCard';
import { BesperProviderModal } from './BesperProviderModal';

/**
 * Provider Detail Modal
 * Shows provider information and their products with prices
 * Special handling for B-esper provider
 */
export const ProviderDetailModal = ({
  provider,
  products = [],
  onClose,
  onProductSelect = null
}) => {
  // Check if this is the B-esper provider - computed before hooks
  const isBesper = provider?.isBesper || provider?.id === 'besper' || provider?.name?.toLowerCase() === 'b-esper';
  
  const providerName = provider?.name || 'Unnamed Provider';
  const logoUrl = provider?.metadata?.partner_profile?.logo_url;
  const description = provider?.metadata?.partner_profile?.description || provider?.description || 'No description available.';
  const tags = provider?.metadata?.tags || [];
  
  // Filter products that belong to this provider
  // Always call hooks - use empty array if provider is B-esper
  const providerProducts = useMemo(() => {
    if (isBesper || !provider?.id) return [];
    
    return products.filter(product => {
      // Check if product's provider matches this provider
      const productProviderId = product.marketplace?.provider?.id || 
                                 product.metadata?.provider_id ||
                                 product.provider_id;
      return productProviderId === provider.id;
    });
  }, [products, provider?.id, isBesper]);
  
  // Hide technical interface when modal is open
  // Always call hooks - effect will run for both B-esper and regular providers
  useEffect(() => {
    document.body.classList.add('hide-technical-interface');
    return () => {
      // Only remove if no other modals are open
      const hasOtherModals = document.querySelector(
        '.tenant-modal-overlay, .resource-modal-overlay, .costpool-modal-overlay, ' +
        '.user-detail-modal-overlay, .modal-overlay, .implementation-fullscreen-overlay, ' +
        '.besper-provider-modal-overlay, ' +
        '[class*="modal-overlay"], [class*="fullscreen-overlay"]'
      );
      if (!hasOtherModals) {
        document.body.classList.remove('hide-technical-interface');
      }
    };
  }, []);
  
  // Early return AFTER all hooks are called
  if (isBesper) {
    return <BesperProviderModal onClose={onClose} />;
  }

  // Generate logo placeholder
  // eslint-disable-next-line no-unused-vars
  const getLogoPlaceholder = () => {
    const initials = providerName
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
    
    return (
      <div style={{
        width: '80px',
        height: '80px',
        borderRadius: '12px',
        background: 'var(--bg-tertiary)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '24px',
        fontWeight: '600',
        color: 'var(--text-primary)'
      }}>
        {initials}
      </div>
    );
  };
  
  return (
    <div 
      className="provider-detail-modal-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        zIndex: 10000,
        display: 'flex',
        alignItems: window.innerWidth <= 768 ? 'flex-end' : 'center',
        justifyContent: 'center',
        padding: window.innerWidth <= 768 ? '0' : '20px'
      }}
    >
      <div 
        className="provider-detail-modal"
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--bg-primary)',
          borderRadius: window.innerWidth <= 768 ? '16px 16px 0 0' : '12px',
          maxWidth: window.innerWidth <= 768 ? '100%' : '900px',
          width: '100%',
          maxHeight: window.innerWidth <= 768 ? '90vh' : '90vh',
          height: window.innerWidth <= 768 ? '90vh' : 'auto',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
          margin: 0
        }}
      >
        {/* Header */}
        <div className="provider-detail-modal-header" style={{
          padding: window.innerWidth <= 768 ? '16px' : '24px',
          borderBottom: '1px solid var(--border-light)',
          display: 'flex',
          flexDirection: window.innerWidth <= 768 ? 'column' : 'row',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          flexShrink: 0,
          gap: window.innerWidth <= 768 ? '12px' : '0',
          position: 'relative'
        }}>
          <div style={{ display: 'flex', gap: window.innerWidth <= 768 ? '12px' : '16px', flex: 1, width: '100%' }}>
            {logoUrl ? (
              <AuthenticatedDatasetImage
                logoUrl={logoUrl}
                alt={providerName}
                className="provider-detail-modal-logo"
                style={{
                  width: window.innerWidth <= 768 ? '56px' : '80px',
                  height: window.innerWidth <= 768 ? '56px' : '80px',
                  borderRadius: '12px',
                  objectFit: 'cover',
                  flexShrink: 0
                }}
              />
            ) : (
              <div style={{
                width: window.innerWidth <= 768 ? '56px' : '80px',
                height: window.innerWidth <= 768 ? '56px' : '80px',
                borderRadius: '12px',
                background: 'var(--bg-tertiary)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: window.innerWidth <= 768 ? '20px' : '24px',
                fontWeight: '600',
                color: 'var(--text-primary)',
                flexShrink: 0
              }}>
                {providerName.split(' ').map(word => word[0]).join('').toUpperCase().slice(0, 2)}
              </div>
            )}
            <div style={{ flex: 1, minWidth: 0 }}>
              <h2 className="provider-detail-modal-title" style={{ 
                margin: '0 0 8px 0', 
                fontSize: window.innerWidth <= 768 ? '1.25rem' : '1.5rem', 
                fontWeight: '600',
                lineHeight: '1.3'
              }}>
                {providerName}
              </h2>
              <p className="provider-detail-modal-description" style={{ 
                margin: 0, 
                color: 'var(--text-secondary)', 
                fontSize: window.innerWidth <= 768 ? '0.875rem' : '0.875rem',
                lineHeight: '1.5'
              }}>
                {description}
              </p>
              {tags.length > 0 && (
                <div style={{ 
                  display: 'flex', 
                  flexWrap: 'wrap', 
                  gap: '8px', 
                  marginTop: '12px' 
                }}>
                  {tags.map((tag, index) => (
                    <span 
                      key={index}
                      className="feature-tag"
                      style={{
                        padding: '4px 10px',
                        borderRadius: '12px',
                        background: 'rgba(var(--primary-rgb), 0.1)',
                        color: 'var(--primary)',
                        fontSize: window.innerWidth <= 768 ? '0.6875rem' : '0.75rem',
                        fontWeight: '500'
                      }}
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
          <button
            className="provider-detail-modal-close"
            onClick={onClose}
            style={{
              background: window.innerWidth <= 768 ? 'var(--bg-secondary)' : 'none',
              border: window.innerWidth <= 768 ? '1px solid var(--border-light)' : 'none',
              borderRadius: window.innerWidth <= 768 ? '10px' : '0',
              fontSize: '24px',
              cursor: 'pointer',
              color: 'var(--text-secondary)',
              padding: window.innerWidth <= 768 ? '0' : '4px',
              width: window.innerWidth <= 768 ? '40px' : 'auto',
              height: window.innerWidth <= 768 ? '40px' : 'auto',
              lineHeight: 1,
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: window.innerWidth <= 768 ? 'absolute' : 'static',
              top: window.innerWidth <= 768 ? '16px' : 'auto',
              right: window.innerWidth <= 768 ? '16px' : 'auto'
            }}
            aria-label="Close modal"
          >
            ×
          </button>
        </div>
        
        {/* Content */}
        <div className="provider-detail-modal-content" style={{
          padding: window.innerWidth <= 768 ? '16px' : '24px',
          overflowY: 'auto',
          flex: 1,
          WebkitOverflowScrolling: 'touch'
        }}>
          <h3 className="provider-detail-modal-section-title" style={{ 
            margin: '0 0 16px 0', 
            fontSize: window.innerWidth <= 768 ? '1rem' : '1.125rem', 
            fontWeight: '600' 
          }}>
            Products & Pricing
          </h3>
          
          {providerProducts.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: window.innerWidth <= 768 ? '32px 16px' : '40px 20px',
              color: 'var(--text-secondary)'
            }}>
              <p style={{ fontSize: window.innerWidth <= 768 ? '0.875rem' : '1rem', margin: 0 }}>
                This provider doesn't have any products available yet.
              </p>
            </div>
          ) : (
            <div className="marketplace-grid provider-products-grid" style={{
              display: 'grid',
              gridTemplateColumns: window.innerWidth <= 768 ? '1fr' : 'repeat(auto-fill, minmax(250px, 1fr))',
              gap: window.innerWidth <= 768 ? '12px' : '20px'
            }}>
              {providerProducts.map(product => (
                <MarketplaceCard
                  key={product.id}
                  product={product}
                  isSelected={false}
                  onSelect={(selectedProduct) => {
                    if (onProductSelect) {
                      onProductSelect(selectedProduct);
                      onClose(); // Close provider modal when product is selected
                    }
                  }}
                  loading={false}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

